/**
 * Environment Configuration Utility
 *
 * Centralized environment detection and configuration for the app.
 * Use this module for all environment-based conditionals.
 */

// Environment constants
export const ENVIRONMENTS = {
  DEV: 'dev',
  TEST: 'test',
  PROD: 'prod',
};

// Get current environment from VITE_ENV
const currentEnv = import.meta.env.VITE_ENV || ENVIRONMENTS.DEV;

// Validate environment value
if (!Object.values(ENVIRONMENTS).includes(currentEnv)) {
  console.warn(`Invalid VITE_ENV value: ${currentEnv}. Defaulting to dev.`);
}

// Environment check helpers
export const isDev = () => currentEnv === ENVIRONMENTS.DEV;
export const isTest = () => currentEnv === ENVIRONMENTS.TEST;
export const isProd = () => currentEnv === ENVIRONMENTS.PROD;
export const getEnv = () => currentEnv;

// Environment-specific configuration
export const config = {
  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
  },

  // Feature flags based on environment
  features: {
    // Show debug panels in dev/test only
    showDebugPanel: isDev() || isTest(),

    // Enable detailed logging in dev/test
    enableDetailedLogging: isDev() || isTest(),

    // Enable analytics only in production
    enableAnalytics: isProd(),

    // Enable error reporting in test/prod
    enableErrorReporting: isTest() || isProd(),

    // Example: Experimental features only in dev
    enableExperimentalFeatures: isDev(),
  },

  // Logging configuration
  logging: {
    level: isDev() ? 'debug' : isProd() ? 'error' : 'info',
    logToConsole: !isProd(),
  },
};

// Export a summary for debugging
export const getEnvironmentInfo = () => {
  return {
    environment: currentEnv,
    isDev: isDev(),
    isTest: isTest(),
    isProd: isProd(),
    apiUrl: config.api.baseUrl,
    features: config.features,
  };
};

// Log environment info in non-production environments
if (!isProd()) {
  console.log('[Environment]', getEnvironmentInfo());
}
